import { FadeIn } from "@xola/ui-kit";
import React from "react";
import { useVisibility } from "../../hooks/useVisibility";

interface CustomerSupportInformationProps {
    browser: any;
    os: any;
}

export const CustomerSupportInformation = ({ browser, os }: CustomerSupportInformationProps) => {
    const [visible, { toggle }] = useVisibility();

    return (
        <>
            <h2>Quick Links</h2>
            <ul className="pl-4">
                <li className="list-disc">
                    <a target="_blank" href="https://support.xola.com" rel="noreferrer">
                        Help Center
                    </a>
                </li>
                <li className="list-disc">
                    <span>
                        Clean browser cache for{" "}
                        <span className="font-semibold">
                            <var data-var="browserName">{browser.name}</var>
                        </span>{" "}
                        on{" "}
                        <span className="font-semibold">
                            <var data-var="osName">{os.name}</var>
                        </span>
                        <a className="ml-1 cursor-pointer text-sm" onClick={toggle}>
                            {visible ? "hide" : "show"}
                        </a>
                    </span>
                    {visible && <CleanCache browser={browser.name} />}
                </li>
            </ul>
        </>
    );
};

const CleanCache = ({ browser }: { browser: string }) => {
    let BrowserInstructions = ChromeInfo;
    if (/safari/i.test(browser)) {
        BrowserInstructions = SafariInfo;
    } else if (/edge/i.test(browser)) {
        BrowserInstructions = EdgeInfo;
    } else if (/firefox/i.test(browser)) {
        BrowserInstructions = FirefoxInfo;
    }

    return (
        <FadeIn>
            <BrowserInstructions />
        </FadeIn>
    );
};

const ChromeInfo = () => {
    return (
        <ol className="no-translate ml-4 list-decimal leading-6">
            <li>On your computer, open Chrome</li>
            <li>
                At the top right, click <span className="font-semibold">More</span>
            </li>
            <li>
                Click <span className="font-semibold">More tools</span> &gt;{" "}
                <span className="font-semibold">Clear browsing data</span>
            </li>
            <li>
                At the top, choose a time range. To delete everything, select{" "}
                <span className="font-semibold">All time</span>
            </li>
            <li>
                Next to &quot;Cookies and other site data&quot; and &quot;Cached images and files&quot;, check the boxes
            </li>
            <li>
                Click <span className="font-semibold">Clear data</span>
            </li>
        </ol>
    );
};

const SafariInfo = () => {
    return (
        <ol className="no-translate ml-4 list-decimal leading-6">
            <li>
                In the Safari app on your Mac, choose <span className="font-semibold">History</span> &gt;{" "}
                <span className="font-semibold">Clear History</span>, then click the pop-up menu
            </li>
            <li>Choose how far back you want your browsing history cleared</li>
        </ol>
    );
};

const EdgeInfo = () => {
    return (
        <ol className="no-translate ml-4 list-decimal leading-6">
            <li>
                Select <span className="font-semibold">Settings and more</span> &gt;{" "}
                <span className="font-semibold">Settings</span> &gt;{" "}
                <span className="font-semibold">Privacy, search, and services</span>
            </li>
            <li>
                Under <span className="font-semibold">Clear browsing data</span> &gt;{" "}
                <span className="font-semibold">Clear browsing data now</span>, select{" "}
                <span className="font-semibold">Choose what to clear</span>
            </li>
            <li>
                Under <span className="font-semibold">Time range</span>, choose a time range from the drop-down menu
            </li>
            <li>Choose the types of browsing data you want to clear</li>
            <li>
                Select <span className="font-semibold">Clear now</span>
            </li>
        </ol>
    );
};

const FirefoxInfo = () => {
    return (
        <ol className="no-translate ml-4 list-decimal leading-6">
            <li>
                In the Menu bar at the top of the screen, click Firefox and select{" "}
                <span className="font-semibold">Preferences</span>
            </li>
            <li>
                Select the <span className="font-semibold">Privacy & Security</span> panel
            </li>
            <li>
                In the <span className="font-semibold">Cookies and Site Data</span> section, click Clear Data
            </li>
            <li>Remove the check mark in front of Cookies and Site Data</li>
            <li>
                With Cached Web Content check marked, click the <span className="font-semibold">Clear button</span>
            </li>
            <li>
                Close the <span className="font-mono">about:preferences</span> page. Any changes you&apos;ve made will
                automatically be saved
            </li>
        </ol>
    );
};
